import type { CallEventEnvelopeV2, CallSnapshotV2 } from '../api/generated/calls-v2';
import { isCallEventEnvelopeV2 } from '../types/calls';

export type ReconcileResult = 'ignored-duplicate' | 'ignored-stale' | 'accepted' | 'needs-recovery';

export interface CallReconciler {
  accept(envelope: unknown): ReconcileResult;
  latest(callId: string): CallSnapshotV2 | undefined;
  reset(callId: string): void;
}

export function createCallReconciler(
  fetchSnapshot: (callId: string) => Promise<CallSnapshotV2>,
): CallReconciler {
  const byCall = new Map<string, CallSnapshotV2>();
  const eventIds = new Set<string>();
  const recovering = new Set<string>();

  const recover = (callId: string) => {
    if (recovering.has(callId)) return;
    recovering.add(callId);
    void fetchSnapshot(callId)
      .then(snapshot => {
        const current = byCall.get(callId);
        if (!current || snapshot.version >= current.version) {
          byCall.set(callId, snapshot);
          eventIds.add(`${callId}:${snapshot.eventId}`);
        }
      })
      .catch(() => {
        // The next foreground/reconnect event retries recovery. No partial
        // snapshot is published when the authenticated fetch fails.
      })
      .finally(() => recovering.delete(callId));
  };

  return {
    accept(value: unknown): ReconcileResult {
      if (!isCallEventEnvelopeV2(value)) return 'ignored-stale';
      const envelope = value as CallEventEnvelopeV2;
      const eventKey = `${envelope.callId}:${envelope.eventId}`;
      if (eventIds.has(eventKey)) return 'ignored-duplicate';
      const current = byCall.get(envelope.callId);
      if (!current && envelope.sequenceNumber > 1) {
        recover(envelope.callId);
        return 'needs-recovery';
      }
      if (current && envelope.sequenceNumber <= current.version) return 'ignored-stale';
      if (current && envelope.sequenceNumber > current.version + 1) {
        recover(envelope.callId);
        return 'needs-recovery';
      }
      eventIds.add(eventKey);
      byCall.set(envelope.callId, envelope.snapshot);
      return 'accepted';
    },

    latest(callId: string): CallSnapshotV2 | undefined {
      return byCall.get(callId);
    },

    reset(callId: string): void {
      byCall.delete(callId);
      for (const key of eventIds) {
        if (key.startsWith(`${callId}:`)) eventIds.delete(key);
      }
    },
  };
}
